import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Check, X, RefreshCw, Ban, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import useUserContext from '../../hooks/useUserContext';
import { getAllReports, updateReportStatus, banUser, warnUser } from '../../services/userService';
import { getModerationLogs } from '../../services/llmService';
import useToast from '../../hooks/useToast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';

// Report type from the API
interface Report {
  _id: string;
  reporterId: string;
  reportedUser: string;
  reason: string;
  timestamp: Date;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  reviewedBy?: string;
  reviewNotes?: string;
}

// Add new interface for AI moderation logs
interface ModerationLog {
  id: string;
  question: string;
  result: boolean;
  message: string;
  timestamp: Date;
}

/**
 * Admin Dashboard component for content moderation
 * Allows admins to review (approve/deny) reported content
 * and handle reported inappropriate messages and users
 */
const AdminDashboard: React.FC = () => {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reportedContent, setReportedContent] = useState<Report[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'reports' | 'ai'>('reports');
  const [moderationLogs, setModerationLogs] = useState<ModerationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState<boolean>(true);

  // State for ban user dialog
  const [banDialogOpen, setBanDialogOpen] = useState<boolean>(false);
  const [userToBan, setUserToBan] = useState<string>('');
  const [banReason, setBanReason] = useState<string>('');
  const [banLoading, setBanLoading] = useState<boolean>(false);

  // State for warn user dialog
  const [warnDialogOpen, setWarnDialogOpen] = useState<boolean>(false);
  const [userToWarn, setUserToWarn] = useState<string>('');
  const [warnReason, setWarnReason] = useState<string>('');
  const [warnLoading, setWarnLoading] = useState<boolean>(false);

  // Use a ref to track if we've already loaded reports to prevent infinite fetching
  const initialLoadDone = useRef(false);
  const logsInitialLoadDone = useRef(false);

  // Authentication check - redirects non-admin users
  useEffect(() => {
    if (user === undefined) {
      // User data is still loading, don't redirect yet
      return;
    }

    if (!user || !user.isAdmin) {
      toast({
        title: 'Access Denied',
        description: `Admin privileges required. ${user ? `Your account (${user.username}) is not marked as an admin.` : 'Please log in as an admin.'}`,
        variant: 'destructive',
      });
      navigate('/home');
    }
  }, [user, navigate, toast]);

  // Fetch data when the user is confirmed to be an admin
  useEffect(() => {
    const fetchData = async () => {
      // Skip if we've already loaded the data or if user isn't loaded yet or isn't admin
      if (initialLoadDone.current || !user || !user.isAdmin || !user.username) {
        return;
      }

      setLoading(true);
      try {
        // Get all reports, explicitly passing the current user's username from context
        const allReports = await getAllReports(user.username);

        if (Array.isArray(allReports)) {
          setReportedContent(allReports);
        } else {
          toast({
            title: 'Error',
            description: 'Failed to fetch reports. Check console for details.',
            variant: 'destructive',
          });
        }

        // Mark as loaded to prevent infinite fetching
        initialLoadDone.current = true;
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to fetch reports',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, toast]); // Add dependencies to re-fetch when user changes

  // Add new useEffect for fetching moderation logs
  useEffect(() => {
    const fetchModerationLogs = async () => {
      if (!user?.isAdmin || logsInitialLoadDone.current) return;

      setLogsLoading(true);
      try {
        const logs = await getModerationLogs();
        setModerationLogs(logs);
        logsInitialLoadDone.current = true;
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch moderation logs',
          variant: 'destructive',
        });
      } finally {
        setLogsLoading(false);
      }
    };

    fetchModerationLogs();
  }, [user, toast]);

  // Handle approving content - for user reports, marking as 'resolved'
  const handleApprove = async (id: string, source: 'reports' | 'ai') => {
    try {
      if (source === 'reports' && user?.username) {
        const notes = 'Report reviewed and action taken.';
        await updateReportStatus(id, 'resolved', notes, user.username);

        // Update local state
        setReportedContent(prev =>
          prev.map(item =>
            item._id === id ? { ...item, status: 'resolved', reviewNotes: notes } : item,
          ),
        );

        toast({
          title: 'Success',
          description: 'Report marked as resolved',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update report status',
        variant: 'destructive',
      });
    }
  };

  // Handle rejecting content - for user reports, marking as 'dismissed'
  const handleReject = async (id: string, source: 'reports' | 'ai') => {
    try {
      if (source === 'reports' && user?.username) {
        const notes = 'Report reviewed and dismissed.';
        await updateReportStatus(id, 'dismissed', notes, user.username);

        // Update local state
        setReportedContent(prev =>
          prev.map(item =>
            item._id === id ? { ...item, status: 'dismissed', reviewNotes: notes } : item,
          ),
        );

        toast({
          title: 'Success',
          description: 'Report marked as dismissed',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update report status',
        variant: 'destructive',
      });
    }
  };

  // Handle refreshing the data - this is only triggered by the refresh button
  const handleRefresh = async () => {
    if (!user?.username) {
      toast({
        title: 'Error',
        description: 'No user is logged in',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Get all reports, passing the current user's username from context for reliable authentication
      const allReports = await getAllReports(user.username);

      if (Array.isArray(allReports)) {
        setReportedContent(allReports);
        toast({
          title: 'Success',
          description: `Loaded ${allReports.length} reports`,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to refresh reports - unexpected response format',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to refresh reports',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Open ban dialog for a specific user
  const openBanDialog = (username: string) => {
    setUserToBan(username);
    setBanReason('');
    setBanDialogOpen(true);
  };

  // Open warn dialog for a specific user
  const openWarnDialog = (username: string) => {
    setUserToWarn(username);
    setWarnReason('');
    setWarnDialogOpen(true);
  };

  // Handle user ban
  const handleBanUser = async () => {
    if (!user?.username || !userToBan || !banReason.trim()) {
      toast({
        title: 'Error',
        description: 'Username and reason are required to ban a user',
        variant: 'destructive',
      });
      return;
    }

    setBanLoading(true);
    try {
      await banUser(userToBan, banReason, user.username);

      toast({
        title: 'Success',
        description: `User ${userToBan} has been banned`,
      });

      // Close the dialog
      setBanDialogOpen(false);

      // Refresh the reports to update the UI
      await handleRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to ban user',
        variant: 'destructive',
      });
    } finally {
      setBanLoading(false);
    }
  };

  // Handle warning a user
  const handleWarnUser = async () => {
    if (!user?.username || !userToWarn || !warnReason.trim()) {
      toast({
        title: 'Error',
        description: 'Username and reason are required to warn a user',
        variant: 'destructive',
      });
      return;
    }

    setWarnLoading(true);
    try {
      await warnUser(userToWarn, warnReason, user.username);

      toast({
        title: 'Success',
        description: `Warning issued to user ${userToWarn}`,
      });

      // Close the dialog
      setWarnDialogOpen(false);

      // Refresh the reports to update the UI
      await handleRefresh();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to warn user',
        variant: 'destructive',
      });
    } finally {
      setWarnLoading(false);
    }
  };

  // Helper functions to avoid nested ternary expressions
  const getContentTitle = (reportedUser: string) => `Reported User: ${reportedUser}`;

  const getStatusClassName = (status: 'pending' | 'reviewed' | 'resolved' | 'dismissed') => {
    if (status === 'pending') {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (status === 'resolved') {
      return 'bg-green-100 text-green-800';
    }
    if (status === 'reviewed') {
      return 'bg-blue-100 text-blue-800';
    }
    return 'bg-red-100 text-red-800';
  };

  const renderModerationLogs = () => {
    if (logsLoading) {
      return (
        <div className='flex justify-center items-center h-64'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500'></div>
        </div>
      );
    }

    if (moderationLogs.length === 0) {
      return (
        <Alert>
          <AlertDescription>No moderation logs available at this time.</AlertDescription>
        </Alert>
      );
    }

    return (
      <div className='space-y-4'>
        {moderationLogs.map((log, index) => (
          <Card key={`moderation-log-${log.id || index}`} className='overflow-hidden'>
            <CardHeader className='pb-2'>
              <div className='flex justify-between items-start'>
                <CardTitle className='text-lg'>{log.question}</CardTitle>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    log.result ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                  {log.result ? 'Approved' : 'Rejected'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className='mb-2'>
                <h3 className='font-medium text-gray-700'>Moderation Analysis</h3>
                <p className='text-gray-600 border-l-2 border-gray-300 pl-3 mt-1'>{log.message}</p>
              </div>
              <div className='text-sm text-gray-500'>
                <span className='font-medium'>Timestamp:</span>{' '}
                {new Intl.DateTimeFormat('en-US', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date(log.timestamp))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className='container mx-auto py-8 px-4 max-w-6xl animate-in slide-in-from-bottom duration-500'>
      <div className='flex items-center mb-6'>
        <Shield className='h-8 w-8 text-blue-600 mr-3' />
        <h1 className='text-3xl font-bold text-gray-800'>Admin Dashboard</h1>
        <Button
          variant='outline'
          size='sm'
          className='ml-auto'
          onClick={handleRefresh}
          disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className='mb-6'>
        <div className='flex border-b border-gray-200'>
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === 'reports'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('reports')}>
            User Reports
          </button>
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === 'ai'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('ai')}>
            AI Moderation Logs
          </button>
        </div>
      </div>

      {loading ? (
        <div className='flex justify-center items-center h-64'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <>
          {activeTab === 'reports' && (
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold text-gray-700 mb-4'>User Reported Content</h2>

              {reportedContent.length === 0 ? (
                <Alert>
                  <AlertDescription>No reported content to review at this time.</AlertDescription>
                </Alert>
              ) : (
                <>
                  {reportedContent.map(item => (
                    <Card
                      key={item._id}
                      className='overflow-hidden transition-all duration-200 hover:shadow-md'>
                      <CardHeader className='pb-2'>
                        <div className='flex justify-between items-start'>
                          <CardTitle className='text-lg'>
                            {getContentTitle(item.reportedUser)}
                          </CardTitle>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClassName(item.status)}`}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className='mb-2'>
                          <h3 className='font-medium text-gray-700'>Reason</h3>
                          <p className='text-gray-600 border-l-2 border-gray-300 pl-3 mt-1'>
                            {item.reason}
                          </p>
                        </div>
                        <div className='grid grid-cols-2 gap-4 text-sm text-gray-500'>
                          <div>
                            <span className='font-medium'>Reported by:</span> {item.reporterId}
                          </div>
                          <div>
                            <span className='font-medium'>Date:</span>{' '}
                            {new Intl.DateTimeFormat('en-US', {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            }).format(new Date(item.timestamp))}
                          </div>
                        </div>
                        {item.reviewNotes && (
                          <div className='mt-2'>
                            <h3 className='font-medium text-gray-700'>Admin Notes</h3>
                            <p className='text-gray-600'>{item.reviewNotes}</p>
                          </div>
                        )}
                      </CardContent>
                      {item.status === 'pending' && (
                        <CardFooter className='flex justify-end gap-2 border-t pt-3'>
                          <Button
                            variant='outline'
                            size='sm'
                            className='text-red-500 border-red-200 hover:bg-red-50'
                            onClick={() => handleReject(item._id, 'reports')}>
                            <X className='h-4 w-4 mr-1' />
                            Dismiss
                          </Button>
                          <Button
                            variant='outline'
                            size='sm'
                            className='text-amber-600 border-amber-200 hover:bg-amber-50'
                            onClick={() => openWarnDialog(item.reportedUser)}>
                            <AlertTriangle className='h-4 w-4 mr-1' />
                            Warn User
                          </Button>
                          <Button
                            variant='outline'
                            size='sm'
                            className='text-red-700 border-red-300 hover:bg-red-50'
                            onClick={() => openBanDialog(item.reportedUser)}>
                            <Ban className='h-4 w-4 mr-1' />
                            Ban User
                          </Button>
                          <Button
                            size='sm'
                            className='bg-green-500 hover:bg-green-600 text-white'
                            onClick={() => handleApprove(item._id, 'reports')}>
                            <Check className='h-4 w-4 mr-1' />
                            Action Taken
                          </Button>
                        </CardFooter>
                      )}
                    </Card>
                  ))}
                </>
              )}
            </div>
          )}

          {activeTab === 'ai' && (
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold text-gray-700 mb-4'>AI Moderation Logs</h2>

              {renderModerationLogs()}
            </div>
          )}
        </>
      )}

      {/* Ban User Dialog */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ban User</DialogTitle>
            <DialogDescription>
              You are about to ban user <strong>{userToBan}</strong>. This action will prevent them
              from logging in or using the platform. Please provide a reason for the ban.
            </DialogDescription>
          </DialogHeader>

          <div className='py-4'>
            <label htmlFor='banReason' className='block text-sm font-medium mb-2'>
              Ban Reason
            </label>
            <Textarea
              id='banReason'
              value={banReason}
              onChange={e => setBanReason(e.target.value)}
              placeholder='Explain why this user is being banned...'
              className='w-full min-h-[100px]'
            />
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => setBanDialogOpen(false)} disabled={banLoading}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleBanUser}
              disabled={banLoading || !banReason.trim()}>
              {banLoading ? 'Banning...' : 'Ban User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warn User Dialog */}
      <Dialog open={warnDialogOpen} onOpenChange={setWarnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Warn User</DialogTitle>
            <DialogDescription>
              You are about to issue a warning to <strong>{userToWarn}</strong>. This will be
              recorded in their account history and can be used to justify a ban if violations
              continue. Please provide a reason for the warning.
            </DialogDescription>
          </DialogHeader>

          <div className='py-4'>
            <label htmlFor='warnReason' className='block text-sm font-medium mb-2'>
              Warning Reason
            </label>
            <Textarea
              id='warnReason'
              value={warnReason}
              onChange={e => setWarnReason(e.target.value)}
              placeholder='Explain why you are warning this user...'
              className='w-full min-h-[100px]'
            />
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setWarnDialogOpen(false)}
              disabled={warnLoading}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              className='bg-amber-500 text-white hover:bg-amber-600'
              onClick={handleWarnUser}
              disabled={warnLoading || !warnReason.trim()}>
              {warnLoading ? 'Issuing Warning...' : 'Issue Warning'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
